import React, { useContext, useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Breadcrumb, Form, Button, message, notification, Alert, Modal, Tooltip } from 'antd'
import { LoadingOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { API, graphqlOperation } from 'aws-amplify'
import { getLocationById, getTotalLocationsAndUsers } from '../../../../graphql/custom-queries'

import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAuthCompanySlice } from '../../../../store/auth-company-slice'
import { selectHelpInfoSlice } from '../../../../store/help-info-slice'
import { notificationStore } from '../../../../context/notificationsContext/store'
import { getTimeZoneOrDefaultToUtc } from '@vacationtracker/shared/functions/timezone'
import { NUMBER_OF_LOCATIONS_LIMIT } from '@vacationtracker/shared/data/app-parameters'

import IntlMessages from '../../../../util/IntlMessages'
import CircularProgress from '../../../../components/circular-progress'
import LocationForm from '../../../../components/location-form'

import { IGetLocationData, IGetLocationById, IGetTotalLocationsAndUsers, IGetUserListShort } from '../../../../types/locations'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
// import { showHelpInfo } from '../../../../services/help-info/show-help-info'
// import { ICompanyInfo } from '@vacationtracker/shared/types/company'

const { confirm } = Modal

interface ILocationFormPage {
  match: {
    params: {
      id: string
    }
  }
}

const LocationFormPage = ({ match }: ILocationFormPage): React.ReactElement => {
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const history = useHistory()
  // const dispatch = useAppDispatch()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)

  const [isLoading, setIsLoading] = useState(true)
  const [isLocationLoading, setLocationIsLoading] = useState(true)
  const [createLoader, setCreateLoader] = useState(false)
  const [location, setLocation] = useState<IGetLocationById>({
    id: '',
    name: '',
    workWeek: [1, 2, 3, 4, 5],
    timestamp: '',
    resetQuotas: 'FISCAL_YEAR',
    firstDayOfWeek: 1,
    yearStartDay: 1,
    yearStartMonth: 1,
    timezone: getTimeZoneOrDefaultToUtc(),
    isDefault: false,
    users: [],
  })
  const [users, setUsers] = useState<IGetUserListShort[]>([])
  const [currentWorkWeek, setCurrentWorkWeek] = useState<number[]>([])
  const [totalLocation, setTotalLocations] = useState<number>(1)
  const { actions } = useAppSelector(selectHelpInfoSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)

  useEffect(() => {        
    if (match.params.id) {
      fetchLocation(match.params.id)
    } 
    fetchTotalLocationsAndUsers()
  }, [match.params.id])

  // useEffect(() => {   
  //   if (!isLoading) {      
  //     showHelpInfo(authCompany as ICompanyInfo, actions, 'HELP_INFO_LOCATION_CREATE', dispatch)
  //   } 
  // }, [isLoading])

  const fetchLocation = async (locationId) => {
    try {
      const response = await API.graphql(graphqlOperation(getLocationById, { id:locationId })) as IGetLocationData
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      response.data.getLocation.users = (response.data.getLocation.users as any).map(({ id }) => id)
      if (response.data.getLocation.rolloverExpiryMonth === 0) {
        response.data.getLocation.rolloverExpiryMonth = 6
      }

      if (response.data.getLocation?.timezone) {
        setLocation(response.data.getLocation)
      } else {
        setLocation({
          ...response.data.getLocation,
          timezone: getTimeZoneOrDefaultToUtc(),
        })
      }

      setCurrentWorkWeek(response.data.getLocation.workWeek)
      setLocationIsLoading(false)
    } catch (err) { console.log('error fetching location', err) }
  }

  const fetchTotalLocationsAndUsers = async () => {
    try {
      const response = await API.graphql(graphqlOperation(getTotalLocationsAndUsers)) as IGetTotalLocationsAndUsers

      setTotalLocations(response.data.getLocationList.length)
      setUsers(response.data.getUsersFiltered.users)
      setIsLoading(false)
    } catch (err) { console.log('error fetching total locations', err) }
  }

  const handleSubmit = async () => {    
    const values = await form.validateFields()
    try {
      setCreateLoader(true)
      const cancelUserSettings = values.cancelUserSettings
      delete values.cancelUserSettings
      const woorkWeek = values.workWeek
      delete values.workWeek

      if (!match.params.id) {
        values.days = woorkWeek
      }
      if (match.params.id) {
        delete values.rolloverExpiryMonth
        delete values.rolloverExpiryDay
        delete values.rolloverExpiryAfterDays
      }

      const response = await API.post('CoreEvent', '/core/event', {
        body: {
          eventType: match.params.id ? 'LOCATION_UPDATED' : 'LOCATION_CREATED',
          eventGroup: 'LOCATION',
          locationId: match.params.id,
          ...values,
        },
      })

      if (match.params.id && JSON.stringify(woorkWeek.sort()) !== JSON.stringify(currentWorkWeek.sort())) {
        await API.post('CoreEvent', '/core/event', {
          body: {
            eventType: 'LOCATION_WORK_WEEK_UPDATED',
            eventGroup: 'LOCATION',
            locationId: match.params.id,
            days: woorkWeek.sort(),
            cancelUserSettings,
          },
        })
      }

      notification.open({
        key: response.correlationId,
        message: match.params.id ?
          formatMessage({ id: 'location.updateInProgress'}, { locationName: response.name }) :
          formatMessage({ id: 'location.createInProgress' }, { locationName: response.name }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setActionNotifications([
        ...actionNotifications,
        response.correlationId,
      ])

      setCreateLoader(false)
      history.push('/app/settings/locations')

      if (!match.params.id) {
        form.resetFields()
      }
    } catch (error) {
      console.log('error', error)
      setCreateLoader(false)
      message.error(error)
    }
  }

  const deleteLocation = async (locationId: string, name: string) => {
    try {
      const response = await API.post('CoreEvent', '/core/event', {
        body: {
          eventType: 'LOCATION_DELETED',
          eventGroup: 'LOCATION',
          locationId,
        },
      })

      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'location.deleteInProgress' }, { locationName: name }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([
        ...actionNotifications,
        response.correlationId,
      ])
    } catch (error) {
      console.log('error', error)
      message.error(error)
    }
  }

  const backToLocations = () => {
    history.push('/app/settings/locations')
  }

  const showConfirmDelete = () => {
    confirm({
      title: formatMessage({ id: 'locations.deleteLocationTitle' }, { name: location.name }),
      icon: <ExclamationCircleOutlined />,
      content: <>
        {formatMessage({ id: 'locations.deleteLocationConfirm'}, { name: location.name })}<br/><br/>
        <WarningOutlined /> {formatMessage({ id: 'locations.deleteLocationConfirmInitialRollover'})}
      </>,
      okText: formatMessage({ id: 'locations.deleteLocation' }),
      okType: 'danger',
      maskClosable: true,
      async onOk() {
        await deleteLocation(location.id, location.name)
        history.push('/app/settings/locations')
      },
    })
  }

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 12 },
    },
  }

  const tailLayout = {
    wrapperCol: {xs: { offset: 0, span: 18 }, md: { offset: 6, span: 18 }},
  }

  return (
    <div className='main-content'>
      {((match.params.id && isLocationLoading) || isLoading) ?
        <CircularProgress /> :
        <>
          <div className="main-content-header">
            <div className="main-content-header-title">
              {location.id ? <span><IntlMessages id="location.editLocation" /></span> : <span><IntlMessages id="locationForm.createLocation" /></span> }
            </div>
            <div className="main-content-header-breadcrumb">
              <Breadcrumb>
                <Breadcrumb.Item>
                  <Link to="/app/dashboard"><IntlMessages id="sidebar.dashboard" /></Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item><IntlMessages id="sidebar.settings" /></Breadcrumb.Item>
                <Breadcrumb.Item>
                  <Link to="/app/settings/locations"><IntlMessages id="app.locations" /></Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  {location.id ? <IntlMessages id="location.editLocation" /> : <IntlMessages id="locationForm.createLocation" /> }
                </Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </div>
          <div className="main-content-body">
            {!location.id && authCompany?.plan === SubscriptionPlanEnum.core && totalLocation >= NUMBER_OF_LOCATIONS_LIMIT &&
              <Alert style={{ marginBottom: 20 }} 
                message={
                  <IntlMessages 
                    id="locationForm.locationLimitReached"
                    values={{ limitNumber: NUMBER_OF_LOCATIONS_LIMIT, billingPage: (...chunks) => (<Link to="/app/settings/billing">{chunks}</Link>) }}
                  />
                }
                type="warning" 
              />
            }
            <Form
              {...formItemLayout}
              form={form}
              name="locationForm"
              initialValues={location}
              className="location-from"
              onFinish={handleSubmit}
              disabled={!location.id && authCompany?.plan === SubscriptionPlanEnum.core && totalLocation >= 3}
              scrollToFirstError
            >
              <LocationForm location={location} totalLocation={totalLocation} users={users} form={form} />

              <Form.Item {...tailLayout}>
                {
                  location.id ? (
                    <Tooltip title={ location.isDefault ? <IntlMessages id="locations.deleteLocation.disabled" /> : null}>
                      <Button type="default" danger style={{ marginRight: 10 }} onClick={showConfirmDelete} disabled={location.isDefault}>
                        <IntlMessages id="locations.deleteLocation" />
                      </Button>
                    </Tooltip>
                  ) : null
                }
                <Button type="default" style={{ marginRight: 10 }} onClick={backToLocations}>
                  <IntlMessages id="app.cancel" />
                </Button>
                <Button
                  htmlType="submit"
                  type="primary"
                  loading={createLoader}
                >
                  {location.id ? <IntlMessages id="locationForm.updateLocation" /> : <IntlMessages id="locationForm.createLocation" />}
                </Button>
              </Form.Item>
            </Form>
            <Alert style={{ whiteSpace: 'pre-line' }} message={<IntlMessages id="locationForm.assignLeaveTypesInfo" />} type="info" />
          </div>
        </>
      }
    </div>
  )
}

export default LocationFormPage
